// ! Utility to help link targets

import * as Selectors from '../../redux/selectors';
import config from '../../cfg';
import store from '../../redux/store';
import * as NetworkActions from '../../redux/actions/network.actions';
import * as UIAction from '../../redux/actions/ui.actions';
import * as LinkHelper from '../../utils/LinkHelper';
import { poll } from '@hotglue/common';
import { Alert } from '../popupAlerts';
import { disconnectLinkedTarget, createOauthStateToken } from '../../api/client';

export const handleDirectLinkTarget = async (target, values = {}) => {
  const { type, target: id, label } = target;
  const updatedTarget = Object.assign({}, target, values);
  const response = await store.dispatch(NetworkActions.doAddLinkedTarget(updatedTarget));

  // Check if request was successful
  if (response.status === 'error') {
    throw response.payload;
  }

  const state = store.getState();
  const isConnected = state.targets.selectedTarget && state.targets.selectedTarget.target === id;

  if (type === 'oauth' && !updatedTarget.symlink && !isConnected) {
    const target_url = await getTargetUrl(id, values);
    if (!target_url) return;
    createAndPollLink(target_url, label, id);

    // Don't trigger listener until OAuth is linked
    return response;
  }

  // Alert listener if exists
  const listener = Selectors.getListener();
  if (listener && listener.onTargetLinked) {
    listener.onTargetLinked(target, Selectors.getSelectedFlow(), Selectors.getTenantId());
  }

  return response;
};

export const getTargetUrl = async (
  targetId,
  values = {},
  flow = Selectors.getSelectedFlow(),
  user_id = Selectors.getTenantId(),
) => {
  const target = Selectors.selectSupportedTargetById(targetId);
  if (!target) return undefined;

  let base_url = target.target_url;

  if (values && values.config) {
    // Handle variables in base_url (used for Dynamics)
    Object.entries(values.config).forEach(([k, v]) => {
      base_url = base_url.replace(`{${k}}`, v);
    });
  }

  const env_id = Selectors.getEnvId();
  const api_key = Selectors.getApiKey();
  const callbackDomain = Selectors.getCallbackDomain();
  const callbackDomainName = Selectors.getCallbackDomainName();
  const callbackFavicon = Selectors.getCallbackFavicon();
  const hideText = Selectors.getHideText()
  const bgColor = Selectors.getBgColor()
  const skipCallbackOAuthErrors = Selectors.skipCallbackOAuthErrors();
  const api_url = config.api_url;
  const callbackUrl = config.redirect_url;

  // Build the state needed by the callback that completes OAuth flow
  const state = {
    user_id,
    env_id,
    flow,
    target_name: target.target,
    api_url: `${api_url}/tap/auth`,
    api_key,
    domain: callbackDomain || 'hotglue.xyz',
    domainName: callbackDomainName,
    favicon: callbackFavicon,
    hideText,
    bgColor,
    skipCallbackOAuthErrors,
    popupShouldClose: true,
  };

  const stateTokenResponse = await createOauthStateToken(state);
  const stateToken = stateTokenResponse.token;
  return `${base_url}&state=HGTOKEN-${stateToken}&redirect_uri=${callbackUrl}`
};

// Polls to see if the target is linked
export const pollLink = (targetId, label, pollWindowClosed, popupWindow = undefined) => {
  let retryCount = 0;

  const selectedFlowId = Selectors.getSelectedFlow();
  const tenantId = Selectors.getTenantId();

  const { future, cancel } = poll({
    request: () => {
      // Don't call cancel() directly here, as it breaks the Promise chain
      // Instead, return a resolved Promise if needed
      if(!Selectors.getSelectedFlow()) {
        console.log('No selected flow, stopping poll');
        // Schedule the cancel for after this request completes
        setTimeout(() => cancel(), 0);
        // Return a resolved Promise with empty data to keep the chain intact
        return Promise.resolve([
          { payload: [] },
          { payload: [] }
        ]);
      }

      if (pollWindowClosed()) {
        // Schedule the cancel for after this request completes
        console.log('Poll window closed, scheduling cancel');
        setTimeout(() => cancel(), 0);
      }
      retryCount++;
      return Promise.all([
        store.dispatch(NetworkActions.fetchLinkedTarget(targetId)),
        store.dispatch(NetworkActions.fetchLinkedFlows(tenantId)),
      ]);
    },
    pollingPeriod: 1000,
    shouldStop: (r) => {
      if(!Selectors.getSelectedFlow())
        return true


      const [{ payload: response }] = r;
      if (pollWindowClosed()) return true;
      if (Array.isArray(response) && response.length == 1) {
        const target_config = response[0];
        if (target_config && !target_config.disconnected) return true;
        return retryCount > 300;
      }
      return retryCount > 300;
    },
  });

  future
    .then((response) => {
      const [{ payload: linkedTargets }, { payload: linkedFlows }] = response;

      const isFlowLinked = Boolean(Array.isArray(linkedFlows) && linkedFlows.find(({ id }) => id === selectedFlowId));

      const listener = Selectors.getListener();
      if (
        !linkedTargets ||
        !isFlowLinked ||
        !(Array.isArray(linkedTargets) && linkedTargets.length > 0)
      ) {
        Alert({ type: 'error', text: `Linking ${label} cancelled.` });

        const isEntityLinked = isFlowLinked && (linkedFlows.find(({ id }) => id === selectedFlowId)?.targets ?? []).includes(targetId);

        let promise = null;

        if (!isEntityLinked) {
          promise = disconnectLinkedTarget(targetId, selectedFlowId, tenantId);
        }

        const onFinally = () => UIAction.stopSourceLink();

        if (promise !== null) {
          promise.finally(() => {
            LinkHelper
              .postLink(undefined, 'target', Selectors.getSelectedFlow())
              .finally(onFinally);
          })
        } else {
          onFinally();
        }

        return;
      }

      const error = linkedTargets[0]?.error;

      if (error) {
        Alert({ type: 'error', text: `An error occurred while linking ${label}. ${error && error.message}` });
        if (error?.message && listener?.onTargetLinkFailed) {
          listener.onTargetLinkFailed(
            targetId,
            Selectors.getSelectedFlow(),
            Selectors.getTenantId(),
            error.message,
          );
        }
        return LinkHelper
          .postLink(undefined, 'target', Selectors.getSelectedFlow())
          .finally(() => {
            UIAction.stopSourceLink();
          });
      }

      if (!linkedTargets || !(Array.isArray(linkedTargets) && linkedTargets.length > 0) && (!popupWindow || popupWindow.closed)) {
        Alert({ type: 'error', text: `Linking ${label} cancelled.` });
        UIAction.stopSourceLink();
        // Alert listener if exists
        if (listener && listener.onTargetLinkCanceled) {
          listener.onTargetLinkCanceled(targetId, Selectors.getSelectedFlow());
        }
        return;
      }

      LinkHelper.postLink(undefined, 'target', Selectors.getSelectedFlow()).then(() => {
        UIAction.stopSourceLink();
        UIAction.setIsLinking(false);
        Alert({ type: 'success', text: `${label} linked!` });

        // Alert listener if exists
        if (listener && listener.onTargetLinked) {
          listener.onTargetLinked(linkedTargets[0], Selectors.getSelectedFlow(), Selectors.getTenantId());
        }
      });
    })
    .catch((e) => {
      console.error(e);
      Alert({ type: 'error', text: `An error occurred while linking ${label}. ${e}` });
      UIAction.stopSourceLink();
    });
};

export function createAndPollLink(target_url, label, target) {
  let windowClosed = false;
  const onClose = () => {
    windowClosed = true;

    // Alert listener if exists
    const listener = Selectors.getListener();
    if (listener && listener.onPopupClose) {
      listener.onPopupClose(target, Selectors.getSelectedFlow());
    }
  };
  const pollWindowClosed = () => windowClosed;

  const popupWindow = LinkHelper.createWindowPopup(target_url, `Link your ${label} account`, 800, 600, onClose, 'target');
  pollLink(target, label, pollWindowClosed, popupWindow);
}

export const pollJob = async (jobRoot, flow, userId, entityId) => {
  const { jobs } = store.getState();
  const jobState = jobs.polling[`${flow}/${userId}`];

  let isPolling = undefined;
  if (jobState) isPolling = jobState[jobRoot];

  if (isPolling !== undefined && isPolling) return {};
  else UIAction.setAPollingJob(jobRoot, flow, userId, true);

  const { future } = poll({
    request: () => store.dispatch(NetworkActions.fetchJobStatus(jobRoot, flow, userId, entityId)),
    pollingPeriod: 15000,
    shouldStop: ({ payload: job }) => {
      console.log(`Polling job ${jobRoot} - status=${job && job.status}`);
      return !job || job.status.includes('FAILED') || job.status.includes('COMPLETE');
    },
  });

  future.then(({ payload: job }) => {
    if (job.status.includes('FAILED')) Alert({ type: 'error', text: `Job completed with errors.` });
    else Alert({ type: 'success', text: 'Job completed successfully' });
    UIAction.setAPollingJob(jobRoot, flow, userId, false);
  });

  return future;
};

export const doLink = async (target, connectValues) => {
  const { target: targetId, type, label, connect_ui_params, auto_link: autoLink } = target;

  if (connectValues || autoLink) {
    await handleDirectLinkTarget(target, connectValues && { config: connectValues });
  } else if (connect_ui_params) {
    UIAction.showConnectParams(target, 'target');
  } else if (type === 'oauth') {
    const target_url = await getTargetUrl(targetId, target);
    if (!target_url) return;
    createAndPollLink(target_url, label, targetId);
  }
};
